import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import CTASection from '../components/CTASection';
import Button from '../components/Button';
import './Home.css';

const Home: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [selectedTheme, setSelectedTheme] = useState<string | null>('princess');

  const testimonials = useMemo(() => [
    { text: 'home.testimonial1.text', author: 'home.testimonial1.author', role: 'home.testimonial1.role' },
    { text: 'home.testimonial2.text', author: 'home.testimonial2.author', role: 'home.testimonial2.role' },
    { text: 'home.testimonial3.text', author: 'home.testimonial3.author', role: 'home.testimonial3.role' },
    { text: 'home.testimonial4.text', author: 'home.testimonial4.author', role: 'home.testimonial4.role' },
    { text: 'home.testimonial5.text', author: 'home.testimonial5.author', role: 'home.testimonial5.role' },
  ], []);

  const themes = useMemo(() => [
    { id: 'princess', icon: '👑', name: 'home.theme.princess', bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)' },
    { id: 'unicorn', icon: '🦄', name: 'home.theme.unicorn', bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 50%, #d299c2 100%)' },
    { id: 'pokemon', icon: '⚡', name: 'home.theme.pokemon', bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 50%, #ff8a80 100%)' },
    { id: 'superhero', icon: '🦸', name: 'home.theme.superhero', bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)' },
  ], []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Set princess theme background on page load
  useEffect(() => {
    const princessTheme = themes.find(theme => theme.id === 'princess');
    if (princessTheme) {
      const themesSection = document.querySelector('.themes-preview') as HTMLElement;
      if (themesSection) {
        themesSection.style.background = princessTheme.bg;
        // Princess theme is light, so remove dark-theme class
        themesSection.classList.remove('dark-theme');
      }
    }
  }, [themes]);

  const handleThemeClick = (themeId: string, background: string) => {
    setSelectedTheme(themeId);
    // Apply background to themes section
    const themesSection = document.querySelector('.themes-preview') as HTMLElement;
    if (themesSection) {
      themesSection.style.background = background;

      // Check if theme has dark background and adjust text colors
      const isDarkTheme = themeId === 'superhero';
      if (isDarkTheme) {
        themesSection.classList.add('dark-theme');
      } else {
        themesSection.classList.remove('dark-theme');
      }
    }
  };

  const handleBookParty = () => {
    navigate('/contact');
  };

  const handleCallPartyDirector = () => {
    window.location.href = 'tel:+14073982139';
  };

  const handleViewGallery = () => {
    navigate('/gallery');
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>{t('home.hero.title')}</h1>
          <p>{t('home.hero.subtitle')}</p>
          <Button
            text={t('home.hero.cta')}
            emoji="🎉"
            onClick={handleBookParty}
            theme="dark"
            size="large"
          />
        </div>
      </section>

      {/* Services Section - Why Choose Cabanna's Party? */}
      <section className="services">
        <div className="container">
          <h2>{t('home.services.title')}</h2>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">🏕️</div>
              <h3>{t('home.service1.title')}</h3>
              <p>{t('home.service1.desc')}</p>
            </div>

            <div className="service-card">
              <div className="service-icon">🎨</div>
              <h3>{t('home.service2.title')}</h3>
              <p>{t('home.service2.desc')}</p>
            </div>

            <div className="service-card">
              <div className="service-icon">🎁</div>
              <h3>{t('home.service3.title')}</h3>
              <p>{t('home.service3.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="container">
          <h2>{t('home.testimonials.title')}</h2>
          <div className="testimonials-carousel">
            <button className="carousel-btn prev" onClick={prevTestimonial}>
              ‹
            </button>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"{t(testimonials[currentTestimonial].text)}"</p>
                <div className="testimonial-author">
                  <strong>{t(testimonials[currentTestimonial].author)}</strong>
                  <span>{t(testimonials[currentTestimonial].role)}</span>
                </div>
              </div>
            </div>

            <button className="carousel-btn next" onClick={nextTestimonial}>
              ›
            </button>
          </div>

          <div className="testimonial-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentTestimonial ? 'active' : ''}`}
                onClick={() => setCurrentTestimonial(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <div className="features-showcase">
            <div className="feature-card fun-guaranteed">
              <div className="feature-background">
                <div className="floating-elements">
                  <span className="float-1">🎉</span>
                  <span className="float-2">🎈</span>
                  <span className="float-3">⭐</span>
                </div>
              </div>
              <div className="feature-content">
                <div className="feature-icon">✨</div>
                <h3>{t('home.feature1.title')}</h3>
                <p>{t('home.feature1.desc')}</p>
              </div>
            </div>

            <div className="feature-card free-delivery">
              <div className="feature-background">
                <div className="delivery-truck">🚚</div>
                <div className="delivery-path"></div>
              </div>
              <div className="feature-content">
                <div className="feature-icon">🚚</div>
                <h3>{t('home.feature2.title')}</h3>
                <p>{t('home.feature2.desc')}</p>
              </div>
            </div>

            <div className="feature-card orlando-based">
              <div className="feature-background">
                <div className="city-skyline">
                  <div className="building"></div>
                  <div className="building tall"></div>
                  <div className="building"></div>
                  <div className="building medium"></div>
                </div>
              </div>
              <div className="feature-content">
                <div className="feature-icon">🏰</div>
                <h3>{t('home.feature3.title')}</h3>
                <p>{t('home.feature3.desc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Combined Themes and Services Section */}
      <section className="themes-preview" aria-labelledby="party-themes-heading">
        <div className="container">
          {/* Themes Section */}
          <header>
            <h2 id="party-themes-heading">{t('home.themes.title')}</h2>
            <p className="themes-description">
              {t('home.themes.description')}
            </p>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <Button
                text={t('home.themes.viewall')}
                onClick={handleViewGallery}
                theme="dark"
                size="small"
              />
            </div>
          </header>
          <div className="themes-grid" role="list" aria-label="Available party themes">
            {themes.map((theme) => (
              <article
                key={theme.id}
                className={`theme-card ${selectedTheme === theme.id ? 'selected' : ''}`}
                onClick={() => handleThemeClick(theme.id, theme.bg)}
                role="listitem"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleThemeClick(theme.id, theme.bg);
                  }
                }}
                aria-label={`Select ${t(theme.name)} party theme`}
              >
                <div className="theme-image" aria-hidden="true">
                  {theme.icon}
                </div>
                <h3>{t(theme.name)}</h3>
                <p className="theme-description">
                  {theme.id === 'princess' && 'Royal princess parties with elegant decorations, tiaras, and magical castle themes for unforgettable celebrations.'}
                  {theme.id === 'unicorn' && 'Colorful rainbow unicorn parties with sparkly decorations and magical accessories for dreamy sleepovers.'}
                  {theme.id === 'pokemon' && 'Action-packed Pokemon adventure parties with character decorations and exciting themed elements.'}
                  {theme.id === 'superhero' && 'Hero-themed superhero parties with capes, masks, and action-packed decorations for brave adventurers.'}
                </p>
              </article>
            ))}
          </div>

          <CTASection
            title={t('home.cta.title')}
            subtitle={t('home.cta.subtitle')}
            buttonText={t('home.cta.button')}
            buttonEmoji="📞"
            onButtonClick={handleCallPartyDirector}
            textColor={selectedTheme === 'superhero' ? 'light' : 'dark'}
          />
        </div>
      </section>
    </div>
  );
};

export default Home;
